import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UploadCloud, Trash2, Loader2 } from 'lucide-react';
import { photographerAPI } from '@services/photographerApi';
import { useOnboardingReturn } from '@hooks/useOnboardingReturn';

interface Photo {
  id: string; filename: string; thumbnailUrl: string | null;
  processingStatus: string; ready: boolean;
}
interface Props { eventId: string; }

const PhotoUploader: React.FC<Props> = ({ eventId }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [uploading, setUploading] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const returnToOnboarding = useOnboardingReturn();

  const refresh = useCallback(async () => {
    try {
      const first = await photographerAPI.listEventPhotos(eventId, { page: 1, limit: 100 });
      const allPhotos = [...(first.data as Photo[])];
      const totalPages = first.pagination?.pages || first.pagination?.totalPages || 1;

      for (let page = 2; page <= totalPages; page += 1) {
        const res = await photographerAPI.listEventPhotos(eventId, { page, limit: 100 });
        allPhotos.push(...(res.data as Photo[]));
      }

      setPhotos(allPhotos);
    } catch { /* non-fatal */ }
  }, [eventId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Poll while any photo is still processing.
  useEffect(() => {
    if (!photos.some(p => !p.ready)) return;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  }, [photos, refresh]);

  const uploadFiles = async (files: FileList | File[]) => {
    const list = Array.from(files).filter(f => f.type.startsWith('image/'));
    setUploading(n => n + list.length);
    let succeeded = 0;
    for (const file of list) {
      try {
        const { data } = await photographerAPI.getUploadUrl(eventId, file.name, file.type);
        await photographerAPI.uploadToS3(data.uploadUrl, data.uploadFields, file, () => {});
        succeeded += 1;
      } catch {
        setErrors(e => [...e, `Failed to upload ${file.name}`]);
      } finally {
        setUploading(n => n - 1);
      }
    }
    await refresh();
    // First successful upload satisfies the onboarding "photos" step; return
    // to the dashboard wizard so progress shows (no-op outside onboarding).
    if (succeeded > 0) {
      returnToOnboarding();
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const onZoneKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  const remove = async (id: string) => {
    await photographerAPI.deleteEventPhoto(id, eventId);
    setPhotos(p => p.filter(x => x.id !== id));
  };

  return (
    <div className="space-y-4">
      <div
        role="button"
        tabIndex={0}
        aria-label="Upload photos: drag files here, or activate to choose files"
        onDragOver={e => e.preventDefault()}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={onZoneKeyDown}
        className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-10 text-gray-500 hover:border-blue-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400"
      >
        <UploadCloud className="mb-2 h-8 w-8" />
        <p className="text-sm">Drag photos here, or click to choose</p>
        <input
          ref={inputRef} data-testid="photo-input" type="file" accept="image/*" multiple hidden
          onChange={e => e.target.files && uploadFiles(e.target.files)}
        />
      </div>

      {uploading > 0 && (
        <p className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" /> Uploading {uploading} photo(s)…
        </p>
      )}
      {errors.map((msg, i) => <p key={i} className="text-sm text-red-600">{msg}</p>)}

      <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
        {photos.map(p => (
          <div key={p.id} className="group relative aspect-square overflow-hidden rounded-md bg-gray-100">
            {p.thumbnailUrl
              ? <img src={p.thumbnailUrl} alt={p.filename} className="h-full w-full object-cover" />
              : <div className="flex h-full w-full items-center justify-center text-xs text-gray-400">
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" /> processing
                </div>}
            <button
              onClick={() => remove(p.id)} aria-label={`Delete ${p.filename}`}
              className="absolute right-1 top-1 hidden rounded bg-black/60 p-1 text-white group-hover:block"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PhotoUploader;
